/**
 * Shapes a plan forecast run for the "View Forecast" page: Category →
 * Product → SKU, each with 12-month series (index 0 = Jan, raw $ / units):
 *
 *   plan, plan_units       closed months = actual; open months = the plan
 *   actual, actual_units   closed months only
 *   forecast, forecast_units  open months, from the run
 *
 * Category plan/actual come from the plan rows themselves (exact). A SKU's
 * plan is its row's plan × the SKU's share (catalogPlanSkuShare); its $ are
 * units × list price, so products and SKUs are list-price $.
 */

const N = 12;
const blank = () => Array(N).fill(null);

/** Element-wise sum; a month stays null only if every input is null there. */
export const sumSeries = (list) => {
  const out = blank();
  (list || []).forEach((a) =>
    (a || []).forEach((v, i) => {
      if (v == null || i >= N) return;
      out[i] = (out[i] || 0) + Number(v);
    }),
  );
  return out;
};

const SERIES = ["plan", "plan_units", "actual", "actual_units", "forecast", "forecast_units"];

const sumNodes = (nodes) =>
  Object.fromEntries(SERIES.map((k) => [k, sumSeries(nodes.map((n) => n[k]))]));

const rowKeyOf = (row, dimension) =>
  String(dimension === "product_type" ? row.product_type : row.category_id);

export const buildForecastTree = ({ run, plan, shares }) => {
  const closed = plan?.closed_month ?? 0;
  const dimension = plan?.dimension === "product_type" ? "product_type" : "category";
  const scope = new Set(run?.scope_keys?.length ? run.scope_keys : [run?.row_key]);
  const rows = (plan?.categories || []).filter((r) => scope.has(rowKeyOf(r, dimension)));
  const rowByKey = new Map(rows.map((r) => [rowKeyOf(r, dimension), r]));
  const shareBySku = new Map((shares || []).map((s) => [s.sku, s]));
  const result = run?.result || {};

  const skuNode = (sku) => {
    const info = shareBySku.get(sku.sku) || {};
    const row = rowByKey.get(info.row_key);
    const price = Number(sku.list_price ?? info.list_price) || null;
    const actualUnits = Array.from({ length: N }, (_, i) =>
      i < closed ? Number(sku.actual_units?.[i]) || 0 : null,
    );
    const actual = actualUnits.map((u) => (u == null || !price ? null : u * price));
    const plan = Array.from({ length: N }, (_, i) => {
      if (i < closed) return actual[i];
      if (!row || info.share == null) return null;
      return (Number(row.plan_monthly?.[i]) || 0) * info.share;
    });
    const planUnits = plan.map((v, i) => {
      if (i < closed) return actualUnits[i];
      return v == null || !price ? null : v / price;
    });
    return {
      id: sku.sku,
      name: sku.sku,
      sku: sku.sku,
      forecast_data_id: sku.forecast_data_id,
      list_price: price,
      share: info.share ?? null,
      // Sales fill in the share's basis (new SKU): months estimated, and from what.
      share_filled_months: info.filled_months || 0,
      share_fill_source: info.fill_source || null,
      on_hand: sku.on_hand,
      reorder_point: sku.reorder_point,
      recommended_order_qty: sku.recommended_order_qty,
      plan,
      plan_units: planUnits,
      actual,
      actual_units: actualUnits,
      forecast: sku.revenue || blank(),
      forecast_units: sku.units || blank(),
    };
  };

  const products = (result.products || []).map((p) => {
    const skus = (p.skus || []).map(skuNode);
    return {
      id: p.product_id || p.name,
      name: p.name || "Unnamed product",
      ...sumNodes(skus),
      // The run's own totals for the forecast (same numbers, kept as-is).
      forecast: p.revenue || blank(),
      forecast_units: p.units || blank(),
      skus,
    };
  });

  const closedOnly = (a) => Array.from({ length: N }, (_, i) => (i < closed ? Number(a?.[i]) || 0 : null));
  const category = {
    id: run?.row_key,
    name: run?.row_name || "Category",
    plan: sumSeries(rows.map((r) => r.plan_monthly)),
    plan_units: sumSeries(rows.map((r) => r.plan_units_monthly)),
    actual: closedOnly(sumSeries(rows.map((r) => r.actual_monthly))),
    actual_units: closedOnly(sumSeries(rows.map((r) => r.actual_units_monthly))),
    forecast: result.revenue || blank(),
    forecast_units: result.units || blank(),
  };

  return { closed, category, products };
};

/**
 * The derived lines of a node's grid: Δ vs plan (closed: actual − plan; open:
 * forecast − plan), the outlook (actual so far + forecast after), and the
 * cumulative plan and outlook — for $ (`unit` = "") or units ("_units").
 */
export const gridLines = (node, closed, unit = "") => {
  const plan = node[`plan${unit}`] || blank();
  const actual = node[`actual${unit}`] || blank();
  const forecast = node[`forecast${unit}`] || blank();
  const outlook = Array.from({ length: N }, (_, i) => (i < closed ? actual[i] : forecast[i]));
  const delta = outlook.map((v, i) => (v == null || plan[i] == null ? null : v - plan[i]));
  const cum = (a) => {
    let run = 0;
    let seen = false;
    return a.map((v) => {
      if (v != null) {
        run += v;
        seen = true;
      }
      return seen ? run : null;
    });
  };
  const total = (a, to = N) => {
    const vals = a.slice(0, to).filter((v) => v != null);
    return vals.length ? vals.reduce((s, v) => s + v, 0) : null;
  };
  return {
    plan,
    actual,
    forecast,
    outlook,
    delta,
    cum_plan: cum(plan),
    cum_outlook: cum(outlook),
    ytd: { plan: total(plan, closed), actual: total(actual, closed), delta: total(delta, closed) },
    year: { plan: total(plan), forecast: total(forecast), outlook: total(outlook), delta: total(delta) },
  };
};
